import { useCallback, useMemo, useRef } from 'react';

import { trackAiBuilderTurnAnalyticsEvents } from '../utils/aiBuilderChatTracking';
import {
  type AiBuilderChatAdapter,
  type AiBuilderChatOrchestrationEvent,
  type AiBuilderTurnRequest,
} from '../utils/types';

import { type AiBuilderMessage } from './types';
import { type useAiBuilderTurnRunner } from './useAiBuilderTurnRunner';

type RunAiBuilderEvents = ReturnType<typeof useAiBuilderTurnRunner>;
type AiBuilderTurnRunnerEventStream = Parameters<RunAiBuilderEvents>[0];
type AiBuilderUserMessageStatus = Extract<
  AiBuilderMessage,
  { kind: 'user' }
>['status'];

export type UpdateAiBuilderUserMessageStatus = (
  messageId: string,
  status: AiBuilderUserMessageStatus,
) => void;

export type CreateAiBuilderServerTurnRequest = (
  signal: AbortSignal,
) => AiBuilderTurnRequest;

type DispatchAiBuilderServerTurnOptions = {
  userMessageId?: string;
};

export const useAiBuilderTurnAbortController = () => {
  const activeTurnAbortControllerRef = useRef<AbortController | null>(null);

  const abort = useCallback(() => {
    const controller = activeTurnAbortControllerRef.current;
    if (!controller) return;

    controller.abort();
    activeTurnAbortControllerRef.current = null;
  }, []);

  const create = useCallback(() => {
    abort();

    const controller = new AbortController();
    activeTurnAbortControllerRef.current = controller;
    return controller;
  }, [abort]);

  const clearIfActive = useCallback((controller: AbortController) => {
    if (activeTurnAbortControllerRef.current === controller) {
      activeTurnAbortControllerRef.current = null;
    }
  }, []);

  return useMemo(
    () => ({
      abort,
      create,
      clearIfActive,
    }),
    [abort, clearIfActive, create],
  );
};

export async function* trackAiBuilderUserMessageStatusEvents(
  events: AiBuilderTurnRunnerEventStream,
  {
    activeRunIdRef,
    runId,
    signal,
    updateUserMessageStatus,
    userMessageId,
  }: {
    activeRunIdRef: { current: number };
    runId: number;
    signal?: AbortSignal;
    updateUserMessageStatus: UpdateAiBuilderUserMessageStatus;
    userMessageId: string;
  },
): AsyncIterable<AiBuilderChatOrchestrationEvent> {
  let accepted = false;
  let failed = false;

  const updateStatus = (status: AiBuilderUserMessageStatus) => {
    if (activeRunIdRef.current !== runId) return;
    updateUserMessageStatus(userMessageId, status);
  };

  try {
    for await (const event of events) {
      if (isAiBuilderTurnFailureEvent(event)) {
        if (!accepted) {
          failed = true;
          updateStatus('failed');
        }
        yield event;
        continue;
      }

      if (!accepted && isAiBuilderTurnAcceptedEvent(event)) {
        accepted = true;
        updateStatus('sent');
      }

      yield event;
    }

    if (!accepted && !failed && !signal?.aborted) {
      updateStatus('sent');
    }
  } catch (error) {
    if (!signal?.aborted) {
      updateStatus('failed');
    }

    throw error;
  }
}

const isAiBuilderTurnAcceptedEvent = (
  event: AiBuilderChatOrchestrationEvent,
) => {
  if (isAiBuilderTurnFailureEvent(event)) return false;

  return (
    event.type !== 'delay' &&
    event.type !== 'setSessionId' &&
    event.type !== 'setUsage'
  );
};

const isAiBuilderTurnFailureEvent = (
  event: AiBuilderChatOrchestrationEvent,
) => {
  return (
    event.type === 'append' &&
    event.message.kind === 'system' &&
    event.message.tone === 'error'
  );
};

export const useRunAiBuilderEventsSafely = ({
  activeRunIdRef,
  appendRunRejectionMessage,
  runAiBuilderEvents,
  turnAbortController,
  updateUserMessageStatus,
}: {
  activeRunIdRef: { current: number };
  appendRunRejectionMessage: (runId: number) => void;
  runAiBuilderEvents: RunAiBuilderEvents;
  turnAbortController: ReturnType<typeof useAiBuilderTurnAbortController>;
  updateUserMessageStatus: UpdateAiBuilderUserMessageStatus;
}) => {
  return useCallback(
    (
      events: Parameters<RunAiBuilderEvents>[0],
      controller?: AbortController,
      userMessageId?: string,
    ) => {
      const runId = activeRunIdRef.current + 1;
      const trackedEvents = userMessageId
        ? trackAiBuilderUserMessageStatusEvents(events, {
            activeRunIdRef,
            runId,
            updateUserMessageStatus,
            userMessageId,
            ...(controller?.signal ? { signal: controller.signal } : {}),
          })
        : events;

      void runAiBuilderEvents(trackedEvents)
        .catch(() => {
          if (controller?.signal.aborted) return;

          appendRunRejectionMessage(runId);
        })
        .finally(() => {
          if (controller) {
            turnAbortController.clearIfActive(controller);
          }
        });
    },
    [
      activeRunIdRef,
      appendRunRejectionMessage,
      runAiBuilderEvents,
      turnAbortController,
      updateUserMessageStatus,
    ],
  );
};

export const useDispatchServerTurn = ({
  runAiBuilderEventsSafely,
  turnAbortController,
}: {
  runAiBuilderEventsSafely: ReturnType<typeof useRunAiBuilderEventsSafely>;
  turnAbortController: ReturnType<typeof useAiBuilderTurnAbortController>;
}) => {
  return useCallback(
    (
      startTurn: AiBuilderChatAdapter['startTurn'],
      createRequest: CreateAiBuilderServerTurnRequest,
      options?: DispatchAiBuilderServerTurnOptions,
    ) => {
      const controller = turnAbortController.create();
      const request = createRequest(controller.signal);

      runAiBuilderEventsSafely(
        trackAiBuilderTurnAnalyticsEvents(startTurn(request), request),
        controller,
        options?.userMessageId,
      );
    },
    [runAiBuilderEventsSafely, turnAbortController],
  );
};
